import React, { useMemo } from 'react';
import { Input } from '../../../_custom';
import styles from '../../ManageDealDrawer.module.scss';
import { CONFIG_TYPE, DYNAMIC_INPUT_TYPES } from '../../DealDrawerConstants';

interface DynamicFieldsRendererProps {
    fieldConfig: any;
    formKey: string;
    formData: Record<string, any>;
    handleChange: (key: string, value: any, importanceKey?: string | null) => void;
    dealDrawerState: any;
}

const renderDynamicField = (
    dynInput: { inputObj: any; optLabel: string; inputKey: string },
    formData: Record<string, any>,
    formKey: string,
    handleChange: (key: string, value: any, importanceKey?: string | null) => void,
    dealDrawerState: any,
    dropdownInputOptions: Map<string, string>,
    fieldConfig: any,
) => {
    const { inputObj, optLabel } = dynInput;
    const currentValue = formData[formKey]?.find((item: any) => item.type === optLabel)?.value || '';

    const updateValue = (value: any) => {
        const updatedFormData = formData[formKey]?.map((item: any) => {
            const shouldUpdate =
                fieldConfig?.type === CONFIG_TYPE.MULTIPLE_SELECT_WITH_INPUT
                    ? item.type === optLabel
                    : inputObj.placeholder === dropdownInputOptions.get(item.type);
            return shouldUpdate ? { ...item, value } : item;
        });
        handleChange(formKey, updatedFormData);
    };

    switch (inputObj.type) {
        // TODO: Add other input types here, based on the requirements
        case CONFIG_TYPE.NUMBER:
        default:
            return (
                <Input
                    type={inputObj.type}
                    allowNegative={inputObj.allowNegative ?? false}
                    allowDecimal={inputObj.allowDecimal ?? false}
                    value={currentValue}
                    placeholder={inputObj.isRequired ? inputObj.placeholder : `${inputObj.placeholder} `}
                    onChange={updateValue}
                    unit={inputObj.unit}
                    style={{ marginTop: 0, width: '100%', height: '44px' }}
                    disabled={dealDrawerState?.readonly}
                    showFloatingLabel={false}
                    numberOfDecimals={inputObj.numberOfDecimals}
                />
            );
    }
};

export const DynamicFieldsRenderer: React.FC<DynamicFieldsRendererProps> = ({
    fieldConfig,
    formKey,
    formData,
    handleChange,
    dealDrawerState,
}) => {
    if (!DYNAMIC_INPUT_TYPES.includes(fieldConfig?.type)) return null;

    const dynamicInputsData = useMemo(() => {
        const selectedOptions = formData[formKey] || [];
        const preventDuplicateInputs = fieldConfig?.preventDuplicateInputs;
        const renderedPlaceholders: string[] = [];
        const dynamicInputs: Array<{ inputObj: any; optLabel: string; inputKey: string }> = [];
        const dropdownInputOptions = new Map<string, string>();

        fieldConfig?.dropdownOptions?.forEach((opt: any) => {
            const isSelected = selectedOptions.map((selectedOption: any) => selectedOption.type).includes(opt.label);

            if (isSelected) {
                const inputObjects = opt.inputObjs ? opt.inputObjs : opt.inputObj ? [opt.inputObj] : [];

                inputObjects.forEach((inputObj: any, index: number) => {
                    if (inputObj?.placeholder) {
                        dropdownInputOptions.set(opt.label, inputObj.placeholder);
                    }

                    if (inputObj) {
                        if (!preventDuplicateInputs || (preventDuplicateInputs && !renderedPlaceholders.includes(inputObj.placeholder))) {
                            renderedPlaceholders.push(inputObj.placeholder);
                            const inputKey = inputObjects.length > 1 ? `${opt.label}_${index}` : opt.label;
                            dynamicInputs.push({ inputObj, optLabel: opt.label, inputKey });
                        }
                    }
                });
            }
        });

        return { dynamicInputs, dropdownInputOptions };
    }, [formData[formKey], fieldConfig?.dropdownOptions, fieldConfig?.preventDuplicateInputs, fieldConfig?.type]);

    const { dynamicInputs, dropdownInputOptions } = dynamicInputsData;

    return (
        <>
            {dynamicInputs.map((dynInput, idx) => (
                <div className={styles.InputItem} key={`dynamic-input-${formKey}-${idx}`}>
                    <div className={styles.InputLabel}>
                        <div>{dynInput.inputObj.label || dynInput.inputObj.placeholder}</div>
                    </div>
                    <div style={{ width: '100%' }}>
                        {renderDynamicField(dynInput, formData, formKey, handleChange, dealDrawerState, dropdownInputOptions, fieldConfig)}
                    </div>
                </div>
            ))}
        </>
    );
};
